import React from 'react';
import { Card, Typography, Button, Row, Col, Tag } from 'antd';
import {
  RobotOutlined,
  BarChartOutlined,
  UnlockOutlined,
  DeploymentUnitOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ContactUsForm } from '../components/ContactUsForm';

const { Title, Text } = Typography;

/** Marketing landing page with product overview, contact form, and quick links. */
const Landing: React.FC = () => {
  const navigate = useNavigate();
  const featureCardStyle: React.CSSProperties = { borderRadius: 14, height: '100%' };

  return (
    <div className="landing-page App">
      <div className="landing-gradient-orb" />
      <div className="landing-container">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <img
            src="/logo512.png"
            alt="TrackiAI logo"
            style={{
              width: 88,
              height: 88,
              borderRadius: 16,
              objectFit: 'cover',
              boxShadow: '0 4px 14px rgba(42, 40, 37, 0.08)',
            }}
          />
        </div>
        <Tag className="landing-live-pill" bordered={false}>
          By athletes, for athletes
        </Tag>
        <Title level={1} style={{ textAlign: 'center', marginBottom: 8, marginTop: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
          Tracki<span style={{ color: 'var(--racing-red)' }}>AI</span>
        </Title>
        <Text
          style={{
            display: 'block',
            textAlign: 'center',
            marginBottom: 8,
            fontSize: 18,
            color: 'var(--text-primary)',
          }}
        >
          The real-time intelligence layer for competitions, coaching, and athlete development.
        </Text>
        <Text
          style={{
            display: 'block',
            textAlign: 'center',
            maxWidth: 720,
            margin: '0 auto 36px',
            color: 'var(--text-secondary)',
            fontSize: 15,
          }}
        >
          Bring competition results, athlete performance data, and live timing into one trusted source of
          truth for benchmarking, scouting, and high-quality decision making.
        </Text>

        <Card className="landing-live-signal-card">
          <Row align="middle" justify="space-between" gutter={[16, 12]}>
            <Col xs={24} md={16}>
              <Text strong style={{ display: 'block', color: 'var(--text-primary)', fontSize: 16 }}>
                Follow active meets with near real-time result streaming
              </Text>
              <Text style={{ color: 'var(--text-secondary)' }}>
                Keep officials, coaches, and athletes aligned with a single live scoreboard.
              </Text>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Button size="large" className="landing-live-jump-btn" onClick={() => navigate('/live')}>
                Open live centre
              </Button>
            </Col>
          </Row>
        </Card>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card style={featureCardStyle} className="landing-feature-card">
              <BarChartOutlined style={{ fontSize: 28, color: 'var(--blue-bell)', marginBottom: 10 }} />
              <Title level={4} style={{ marginBottom: 8, color: 'var(--text-primary)' }}>Dashboards</Title>
              <Text style={{ color: 'var(--text-secondary)' }}>
                Visualize trends across athletes, competitions, and national performance in one unified
                analytics experience.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card style={featureCardStyle} className="landing-feature-card">
              <RobotOutlined style={{ fontSize: 28, color: 'var(--blue-bell)', marginBottom: 10 }} />
              <Title level={4} style={{ marginBottom: 8, color: 'var(--text-primary)' }}>TrackiAI</Title>
              <Text style={{ color: 'var(--text-secondary)' }}>
                AI agent for report generation, internet result scraping, and benchmarking support using
                your athletics data.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card style={featureCardStyle} className="landing-feature-card">
              <UnlockOutlined style={{ fontSize: 28, color: 'var(--blue-bell)', marginBottom: 10 }} />
              <Title level={4} style={{ marginBottom: 8, color: 'var(--text-primary)' }}>Tracki.Life</Title>
              <Text style={{ color: 'var(--text-secondary)' }}>
                Athlete app where users gain points from results and redeem them for digital cosmetics.
              </Text>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card style={featureCardStyle} className="landing-feature-card">
              <DeploymentUnitOutlined style={{ fontSize: 28, color: 'var(--blue-bell)', marginBottom: 10 }} />
              <Title level={4} style={{ marginBottom: 8, color: 'var(--text-primary)' }}>Tracki.Agent</Title>
              <Text style={{ color: 'var(--text-secondary)' }}>
                Supports FinishLynx today for automatic data ingestion from races directly into TrackiAI.
              </Text>
            </Card>
          </Col>
        </Row>

        <Card
          style={{
            marginTop: 20,
            borderRadius: 12,
            backgroundColor: 'var(--surface-card-soft)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <Row align="middle" justify="space-between" gutter={[16, 16]}>
            <Col xs={24} md={18}>
              <Title level={5} style={{ marginBottom: 4, color: 'var(--text-primary)' }}>
                Built by athletes, for athletes
              </Title>
              <Text style={{ color: 'var(--text-secondary)' }}>
              We built TrackiAI for performance environments where timing and trust matter. With athletes involved in the development process, the platform is shaped by features that support policy-making, performance analysis, and athlete development.
              </Text>
            </Col>
            <Col xs={24} md={6} style={{ textAlign: 'right' }}>
              <Button
                type="default"
                className="landing-signin-btn"
                icon={<LoginOutlined />}
                onClick={() => navigate('/login')}
              >
                Sign in
              </Button>
            </Col>
          </Row>
        </Card>

        <div style={{ marginTop: 36, marginBottom: 12, textAlign: 'center' }}>
          <Title level={3} style={{ marginBottom: 8, color: 'var(--text-primary)', fontWeight: 700 }}>
            Help us grow the sport
          </Title>
          <Text style={{ color: 'var(--text-secondary)', fontSize: 15, maxWidth: 640, display: 'inline-block' }}>
            We welcome athletes who want to contribute ideas or time, and federations or athletic communities
            who share our mission—use the form below and we will respond as soon as we can.
          </Text>
        </div>

        <div style={{ marginTop: 8 }}>
          <ContactUsForm />
        </div>

        
      </div>
    </div>
  );
};

export default Landing;
